import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowRight, ShoppingCart } from "lucide-react";
import { cartApi, type Cart } from "../../api/cart";
import { CartLineRow } from "../../components/cart/CartLineRow";
import { OrderSummary } from "../../components/cart/OrderSummary";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Button } from "../../components/ui";
import { errorMessage, useCustomerSession } from "../account/session";
import { usePageTitle } from "../catalogue/usePageTitle";
import { CART_KEY, useCart } from "./useCart";

/** PRD screen 8: what's in the basket, at today's prices, and the way to checkout. */
export function CartRoute() {
  usePageTitle("Basket");
  const cart = useCart();

  return (
    <PageWidth className="pt-8">
      <h1 className="mb-6 type-h1 [font-size:var(--text-2xl)]">Basket</h1>
      {cart.isPending ? (
        <p className="type-body text-text-muted">Loading your basket…</p>
      ) : cart.isError ? (
        <LoadError onRetry={() => cart.refetch()} />
      ) : cart.data.items.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="Your basket is empty" action={<Link to="/equipment-categories">Browse machines</Link>}>
          Find your machine, open its diagram and add the parts you need.
        </EmptyState>
      ) : (
        <Basket cart={cart.data} />
      )}
    </PageWidth>
  );
}

function Basket({ cart }: { cart: Cart }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useCustomerSession();
  const signedIn = Boolean(session.data?.user);
  // The line being changed, and a refusal to show under it.
  const [lineError, setLineError] = useState<{ id: number; message?: string } | null>(null);

  const change = useMutation({
    mutationFn: ({ id, quantity }: { id: number; quantity: number | null }) => (quantity === null ? cartApi.remove(id) : cartApi.update(id, quantity)),
    onMutate: ({ id }) => setLineError({ id }),
    onSuccess: (next) => {
      setLineError(null);
      queryClient.setQueryData(CART_KEY, next);
    },
    onError: (error, { id }) => {
      setLineError({ id, message: errorMessage(error) });
      queryClient.invalidateQueries({ queryKey: CART_KEY });
    },
  });

  // Which lines are ticked for checkout. We track only deselections, so the default is all
  // ticked, a part added in another tab comes in ticked, and a quantity refetch (KALI-25)
  // never drops the selection — a removed line's id just stops mattering.
  const [deselected, setDeselected] = useState<Record<number, true>>({});
  const isSelected = (id: number) => !deselected[id];
  const setSelected = (id: number, selected: boolean) =>
    setDeselected((current) => {
      const next = { ...current };
      if (selected) delete next[id];
      else next[id] = true;
      return next;
    });

  const selected = cart.items.filter((line) => isSelected(line.id));
  const allSelected = selected.length === cart.items.length;
  const toggleAll = (checked: boolean) => setDeselected(checked ? {} : Object.fromEntries(cart.items.map((line) => [line.id, true])));
  const selectedCount = selected.reduce((sum, line) => sum + line.quantity, 0);
  const selectedSubtotal = selected.reduce((sum, line) => sum + (line.line_total_cents ?? 0), 0);
  // A ticked line that can't be ordered blocks checkout; unticking it (or an untouched bad line) does not.
  const blocked = selected.some((line) => line.problem);

  const checkoutDisabled = selected.length === 0 || blocked;
  const checkout = (
    <Button
      size="lg"
      fullWidth
      iconRight={ArrowRight}
      disabled={checkoutDisabled}
      onClick={() => navigate("/checkout", { state: { itemIds: selected.map((line) => line.id) } })}
    >
      Check out
    </Button>
  );

  return (
    <>
      <div className="grid items-start gap-8 pb-24 md:pb-0 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="flex min-w-0 flex-col gap-3">
        <label className="flex items-center gap-3 px-4 type-small text-text-body">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && selected.length > 0;
            }}
            onChange={(event) => toggleAll(event.target.checked)}
            aria-label="Select all parts"
            className="size-5 flex-none cursor-pointer accent-green-700 focus-visible:shadow-focus"
          />
          Select all
        </label>
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {cart.items.map((line) => (
            <CartLineRow
              key={line.id}
              line={line}
              selected={isSelected(line.id)}
              onSelect={(value) => setSelected(line.id, value)}
              busy={change.isPending && change.variables?.id === line.id}
              error={lineError?.id === line.id ? lineError.message : undefined}
              onQuantity={(quantity) => change.mutate({ id: line.id, quantity })}
              onRemove={() => change.mutate({ id: line.id, quantity: null })}
            />
          ))}
        </ul>
      </div>

      <div className="lg:sticky lg:top-24">
        <OrderSummary itemCount={selectedCount} subtotalCents={selectedSubtotal}>
          {/* On a phone the Check out button moves to the bar pinned above the tab nav (below). */}
          <div className="hidden md:block">{checkout}</div>
          {selected.length === 0 ? (
            <p className="mt-3 mb-0 type-small text-text-muted">Tick the parts you want to check out.</p>
          ) : blocked ? (
            <p className="mt-3 mb-0 type-small text-warning-700">Sort out or untick the marked parts first.</p>
          ) : null}
          {!signedIn && !session.isPending && (
            <p className="mt-3 mb-0 type-small text-text-muted">You'll sign in or create an account next. Your basket comes with you.</p>
          )}
        </OrderSummary>
      </div>
      </div>

      {/*
       * Phone only: pin the Check out CTA above the bottom tab bar so it never scrolls away once
       * the lines, fulfilment and summary push it below the fold (mobile kit MobileBasket's sticky
       * footer; KALI-46). --tab-bar-height comes from StoreShell, so the bar clears the tabs; the
       * grid's pb-24 keeps the summary scrollable clear of it. Desktop keeps it inline in the summary.
       */}
      <div className="fixed inset-x-0 bottom-[calc(var(--tab-bar-height)+env(safe-area-inset-bottom))] z-20 border-t border-border-subtle bg-surface-card px-4 py-3 shadow-[0_-4px_12px_rgba(36,35,31,.09)] md:hidden">
        {checkout}
      </div>
    </>
  );
}
